<template>
    <DForm @submit.prevent="preview">
        <DToaster
            v-if="!!errors.general"
        >
            <DToast
                v-for="(message, index) in errors.general"
                :key="'flash-' + index"
                :show="true"
                color="danger"
                header-text="Error"
                :body-text="message"
            />
        </DToaster>
        <DTable
            :items="invoiceItems"
            :fields="getFields"
            class="table-responsive overflow-visible"
            hover
            sorter
        >
            <template #product-header>
                <span class="required">{{ __('customers.invoices.form.product') }}</span>
            </template>
            <template #quantity-header>
                <span class="required">{{ __('customers.invoices.form.quantity') }}</span>
            </template>
            <template #line_item-header>
                <span class="required">{{ __('customers.invoices.form.line_item') }}</span>
            </template>
            <template #description-header>
                <span>{{ __('customers.invoices.form.description') }}</span>
            </template>
            <template #from_date-header>
                <span class="required">{{ __('customers.invoices.form.from_date') }}</span>
            </template>
            <template #to_date-header>
                {{ __('customers.invoices.form.to_date') }}
            </template>
            <template #price-header>
                <span class="required">{{ __('customers.invoices.form.net_price') }}</span>
            </template>
            <template #contract_id-header>
                <span>{{ __('customers.invoices.form.contract_id') }}</span>
            </template>
            <template #order_id-header>
                <span>{{ __('customers.invoices.form.order_id') }}</span>
            </template>
            <template #third_party_cost-header>
                <span>{{ __('customers.invoices.form.third_party_cost') }}</span>
            </template>
            <template #actions-header>
                {{ __('customers.invoices.form.actions') }}
            </template>
            <template #product="{item}">
                <td>
                    <DMultiSelect
                        :id="`product_${item.index}`"
                        :key="productSelectKey"
                        :options="getProductOptions(item.index)"
                        :multiple="false"
                        options-style="text"
                        :is-valid="isValid(item.index, 'product')"
                        :invalid-feedback="invalidFeedback(item.index, 'product')"
                        @update="(value) => updateValue(value, item.index)"
                    />
                </td>
            </template>
            <template #quantity="{item}">
                <td>
                    <DInput
                        :id="`quantity_${item.index}`"
                        v-model="form.invoiceItems[item.index].quantity"
                        add-input-classes="bg-light"
                        :placeholder="__('customers.invoices.form.quantity')"
                        :is-valid="isValid(item.index, 'quantity')"
                        :invalid-feedback="invalidFeedback(item.index, 'quantity')"
                        type="number"
                        min="1"
                        max="2147483647"
                    />
                </td>
            </template>
            <template #line_item="{item}">
                <td>
                    <DInput
                        :id="`line_item_${item.index}`"
                        v-model="form.invoiceItems[item.index].line_item"
                        add-input-classes="bg-light"
                        :placeholder="__('customers.invoices.form.line_item')"
                        :is-valid="isValid(item.index, 'line_item')"
                        :invalid-feedback="invalidFeedback(item.index, 'line_item')"
                    />
                </td>
            </template>
            <template #description="{item}">
                <td>
                    <DFormTextarea
                        :id="`description_${item.index}`"
                        v-model="form.invoiceItems[item.index].description"
                        add-input-classes="bg-light"
                        :placeholder="__('customers.invoices.form.description')"
                        :is-valid="isValid(item.index, 'description')"
                        :invalid-feedback="invalidFeedback(item.index, 'description')"
                    />
                </td>
            </template>
            <template #from_date="{item}">
                <td>
                    <DDateFormatInput
                        :id="`from_date_${item.index}`"
                        v-model="form.invoiceItems[item.index].from_date"
                        format="YYYY-MM-DD"
                        :placeholder="__('customers.invoices.form.from_date')"
                        :is-valid="isValid(item.index, 'from_date')"
                        :invalid-feedback="invalidFeedback(item.index, 'from_date')"
                    />
                </td>
            </template>
            <template #to_date="{item}">
                <td>
                    <DDateFormatInput
                        :id="`to_date_${item.index}`"
                        v-model="form.invoiceItems[item.index].to_date"
                        format="YYYY-MM-DD"
                        :placeholder="__('customers.invoices.form.to_date')"
                        :is-valid="isValid(item.index, 'to_date')"
                        :invalid-feedback="invalidFeedback(item.index, 'to_date')"
                    />
                </td>
            </template>
            <template #price="{item}">
                <td>
                    <DInput
                        :id="`net_price_${item.index}`"
                        v-model="form.invoiceItems[item.index].price"
                        add-input-classes="bg-light"
                        :placeholder="__('customers.invoices.form.net_price')"
                        :is-valid="isValid(item.index, 'price')"
                        :invalid-feedback="invalidFeedback(item.index, 'price')"
                        maxlength="13"
                    />
                    <div v-if="form.invoiceItems[item.index].display_discount === true">
                        <DButton
                            class="my-1"
                            :label=" __('customers.invoices.form.discard_discount')"
                            mini
                            color="secondary"
                            @click="toggleDiscount(item.index)"
                        />
                        <DInput
                            :id="`discount_name_${item.index}`"
                            v-model="form.invoiceItems[item.index].discount_name"
                            add-input-classes="bg-light"
                            class="mb-1"
                            :placeholder="__('customers.invoices.form.discount_name')"
                            :is-valid="isValid(item.index, 'discount_name')"
                            :invalid-feedback="invalidFeedback(item.index, 'discount_name')"
                            maxlength="255"
                        />
                        <DSelect
                            :id="`discount_type_${item.index}`"
                            v-model="form.invoiceItems[item.index].discount_type"
                            add-input-classes="bg-light"
                            class="mb-1"
                            :options="discountTypes"
                            :is-valid="isValid(item.index, 'discount_type')"
                            :invalid-feedback="invalidFeedback(item.index, 'discount_type')"
                        />
                        <DInput
                            :id="`discount_value_${item.index}`"
                            v-model="form.invoiceItems[item.index].discount_value"
                            add-input-classes="bg-light"
                            :placeholder="__('customers.invoices.form.discount_value')"
                            :is-valid="isValid(item.index, 'discount_value')"
                            :invalid-feedback="invalidFeedback(item.index, 'discount_value')"
                            min="0"
                        />
                    </div>
                    <div
                        v-else
                        class="mt-1"
                    >
                        <DButton
                            :label=" __('customers.invoices.form.add_discount')"
                            mini
                            color="secondary"
                            @click="toggleDiscount(item.index)"
                        />
                    </div>
                </td>
            </template>
            <template #contract_id="{item}">
                <td>
                    <DMultiSelect
                        :id="`contract_id_${item.index}`"
                        :key="item.index"
                        :options="getContractOptions(item.index)"
                        :multiple="false"
                        options-style="text"
                        :is-valid="isValid(item.index, 'contract_id')"
                        :invalid-feedback="invalidFeedback(item.index, 'contract_id')"
                        @update="(value) => updateContractValue(value, item.index)"
                    />
                </td>
            </template>
            <template #order_id="{item}">
                <td>
                    <DInput
                        :id="`order_id_${item.index}`"
                        v-model="form.invoiceItems[item.index].order_id"
                        add-input-classes="bg-light"
                        :placeholder="__('customers.invoices.form.order_id')"
                        disabled
                    />
                </td>
            </template>
            <template #third_party_cost="{item}">
                <td>
                    <DInput
                        :id="`third_party_cost_${item.index}`"
                        v-model="form.invoiceItems[item.index].third_party_cost"
                        add-input-classes="bg-light"
                        :placeholder="__('customers.invoices.form.third_party_cost')"
                        :is-valid="isValid(item.index, 'third_party_cost')"
                        :invalid-feedback="invalidFeedback(item.index, 'third_party_cost')"
                        maxlength="13"
                        min="0"
                    />
                </td>
            </template>
            <template #actions="{item}">
                <td>
                    <DButton
                        mini
                        color="primary"
                        icon="plus"
                        :disabled="isInLoadingState"
                        @click="addNewRow(item.index)"
                    />
                    <DButton
                        mini
                        color="danger"
                        icon="trash-can-outline"
                        :disabled="isInLoadingState"
                        @click="deleteRow(item.index)"
                    />
                </td>
            </template>
        </DTable>
        <DFormControls
            submit-icon="eye"
            :cancel-route="cancelRoute"
            :dirty="form.isDirty"
            :submit-message="__('messages.preview')"
            @submit="preview"
        />
    </DForm>
</template>

<script>
import { toRaw } from 'vue'
import _ from 'lodash'

const emptyRow = {
    index: 0,
    product: null,
    quantity: null,
    line_item: null,
    description: null,
    from_date: null,
    display_discount: false,
    discount_name: null,
    discount_type: null,
    discount_value: null,
    to_date: null,
    price: null,
    accounting_product: null,
    contract_id: null,
    order_id: null,
    third_party_cost: null
}

export default {
    name: 'InvoiceForm',
    props: {
        errors: {
            type: Object,
            default: () => { return {} }
        },
        customer: {
            type: Object,
            default: () => { return {} }
        },
        invoice: {
            type: Object,
            default: () => { return {} }
        },
        discountTypes: {
            type: Array,
            default: () => { return [] }
        },
        products: {
            type: Array,
            default: () => { return [] }
        },
        userListView: {
            type: Array,
            default: () => {
                return []
            }
        }
    },
    emits: ['preview'],
    data () {
        const invoiceItems = this.mapInvoiceItems(this.invoice.items)
        if (invoiceItems.length === 0) {
            invoiceItems.push(Object.assign({}, emptyRow, { index: invoiceItems.length }))
        }
        return {
            form: this.$inertia.form({
                invoiceItems: invoiceItems,
                paymentMethod: null,
                paymentTerm: null
            }),
            invoiceItems: invoiceItems,
            productSelectKey: 1,
            showListViewModal: false
        }
    },
    computed: {
        cancelRoute () {
            return this.$route('customers.customers.invoices.index', { customer: this.customer.id })
        },
        parsedErrors () {
            const parsedErrors = {}

            Object.keys(this.errors).forEach((field) => {
                parsedErrors[field] = this.errors[field][0]
            })

            return parsedErrors
        },
        getFields () {
            const availableList = []

            this.userListView.forEach((item) => {
                if (item.show_column === true) {
                    availableList.push({
                        key: item.column,
                        label: this.__('settings.list_view.' + item.list + '.' + item.column),
                        sorter: item.sort_column_enabled,
                        _style: item.width !== null ? { minWidth: item.width + '%', maxWidth: item.width + '%' } : {}
                    })
                }
            })

            return availableList
        }
    },
    methods: {
        deleteRow (index) {
            this.form.invoiceItems.splice(index, 1)
            this.form.invoiceItems = this.resetItemIndexes()
            this.invoiceItems = _.cloneDeep(this.form.invoiceItems)
        },
        addNewRow (index) {
            this.form.invoiceItems.splice(index + 1, 0, Object.assign({}, emptyRow))
            this.form.invoiceItems = this.resetItemIndexes()
            this.invoiceItems = _.cloneDeep(this.form.invoiceItems)
            this.productSelectKey++
        },
        preview () {
            this.$emit('preview', this.form)
        },
        isValid (index, field) {
            if (typeof this.parsedErrors[`invoiceItems.${index}.${field}`] !== 'undefined') {
                return false
            }

            return null
        },
        invalidFeedback (index, field) {
            if (this.isValid(index, field) === false) {
                return this.parsedErrors[`invoiceItems.${index}.${field}`]
            }

            return null
        },
        mapInvoiceItems (items) {
            return items.map((item, index) => {
                const hasDiscount = item.discount_amount !== null && item.discount_description !== null
                return {
                    description: item.description,
                    discount_name: item.discount_description,
                    discount_type: hasDiscount ? 'fixed' : null,
                    discount_value: item.discount_amount,
                    display_discount: !!item.discount_value,
                    from_date: item.from_date,
                    index,
                    line_item: item.line_item,
                    price: item.price,
                    quantity: item.quantity,
                    accounting_product: item.accounting_product,
                    to_date: item.to_date,
                    product: item.product,
                    contract_id: item.contract_id,
                    order_id: item.order_id ?? null,
                    third_party_cost: item.third_party_cost
                }
            })
        },
        updateValue (value, index) {
            let product

            this.products.forEach(item => {
                item.options.forEach(option => {
                    if (option.value === parseInt(value[0])) {
                        product = option
                    }
                })
            })
            this.form.invoiceItems[index].line_item = product.text
            this.form.invoiceItems[index].accounting_product = product.accounting_product
            this.form.invoiceItems[index].product = product.value
            this.form.invoiceItems[index].description = product.invoice_text ?? ''
        },
        updateContractValue (value, index) {
            const contract = value[0]

            this.form.invoiceItems[index].contract_id = contract.id
            // `contract.order_id` is the internal orders.id FK, which means nothing to an agent.
            // The backend resolves the customer-facing number, which differs per order client.
            this.form.invoiceItems[index].order_id = contract.order?.order_number_with_client ?? null
        },
        toggleDiscount (index) {
            const item = this.form.invoiceItems[index]

            item.display_discount = !item.display_discount
            if (!item.display_discount) {
                item.discount_name = item.discount_type = item.discount_value = null
            }
        },
        /**
         * This function is needed since CoreUI has a bug with tables, basically you cannot access index in slots
         * @see https://github.com/coreui/coreui-vue/issues/237
         */
        resetItemIndexes () {
            const newItems = []
            let key = -1

            for (const item of this.form.invoiceItems) {
                newItems.push({
                    ...toRaw(item),
                    index: ++key
                })
            }
            return newItems
        },
        getProductOptions (value) {
            const products = _.cloneDeep(this.products)
            products.forEach((item, index) => {
                item.options.forEach((optionItem, optionIndex) => {
                    if (optionItem.value === this.form.invoiceItems[parseInt(value)].product) {
                        products[index].options[optionIndex].selected = true
                    }
                })
            })

            return products
        },
        getContractOptions (value) {
            return this.customer.contracts.map((item) => {
                return {
                    value: item,
                    text: `${item.id}`,
                    selected: item.id === this.form.invoiceItems[parseInt(value)].contract_id
                }
            })
        },
    }
}
</script>

<style scoped>
table td {
    vertical-align: baseline;
}
</style>
